import { createInitialGameState, parseGameState } from "./state.js";
import { bindDiceRollers, getDiceValues, isDiceSpinning, renderDice } from "../ui/dice.js";

/** @typedef {'light' | 'dark'} CheckerColor */

/**
 * @typedef {import('./state.js').GameState} GameState
 */

const TOP_LEFT = [13, 14, 15, 16, 17, 18];
const TOP_RIGHT = [19, 20, 21, 22, 23, 24];
const BOTTOM_LEFT = [12, 11, 10, 9, 8, 7];
const BOTTOM_RIGHT = [6, 5, 4, 3, 2, 1];

const CHECKER_SELECTOR = ".bg-checker";

/**
 * @typedef {object} PieceMoveInfo
 * @property {PiecePickInfo} pick
 * @property {string} toZone
 * @property {boolean} reverted
 */

/**
 * @typedef {object} PiecePickInfo
 * @property {string} fromZone
 * @property {number} stackIndex
 * @property {CheckerColor} color
 */

/**
 * @typedef {object} BoardWireContext
 * @property {() => void} [afterDrop]
 * @property {(pick: PiecePickInfo) => void} [onPiecePick]
 * @property {(toZone: string) => void} [onPieceHover]
 * @property {(move: PieceMoveInfo) => void} [onPieceMove]
 */

/**
 * @param {CheckerColor} color
 */
function createCheckerEl(color) {
  const checker = document.createElement("div");
  checker.className = `bg-checker ${color}`;
  checker.setAttribute("role", "img");
  checker.setAttribute("aria-label", `${color} checker`);
  checker.draggable = true;
  return checker;
}

/**
 * @param {number} pointNumber
 * @param {boolean} isTop
 * @param {GameState} state
 */
function createPointEl(pointNumber, isTop, state) {
  const point = document.createElement("div");
  point.className = `bg-point ${isTop ? "top" : "bottom"}`;
  if (pointNumber % 2 === 0) point.classList.add("alt");
  point.dataset.point = String(pointNumber);
  point.dataset.dropZone = `point-${pointNumber}`;

  const stack = document.createElement("div");
  stack.className = "bg-stack";

  const pip = state.points[pointNumber] || [];
  pip.forEach((color) => stack.appendChild(createCheckerEl(color)));

  point.appendChild(stack);
  return point;
}

/**
 * @param {HTMLElement} root
 * @param {GameState} state
 * @param {BoardWireContext} ctx
 */
function renderStateIntoBoard(root, state, ctx) {
  for (let i = 1; i <= 24; i += 1) {
    const point = root.querySelector(`[data-point="${i}"]`);
    if (!point) continue;
    const stack = point.querySelector(".bg-stack");
    if (!stack) continue;
    stack.replaceChildren();
    const pip = state.points[i] || [];
    const isTop = point.classList.contains("top");
    if (isTop) {
      pip.forEach((c) => stack.appendChild(createCheckerEl(c)));
    } else {
      pip.forEach((c) => stack.appendChild(createCheckerEl(c)));
    }
  }

  const barStack = root.querySelector('[data-drop-zone="bar"] .bg-zone-stack');
  if (barStack) {
    barStack.replaceChildren();
    state.bar.forEach((c) => barStack.appendChild(createCheckerEl(c)));
  }
  const tl = root.querySelector('[data-drop-zone="tray-left"] .bg-zone-stack');
  if (tl) {
    tl.replaceChildren();
    state.trayLeft.forEach((c) => tl.appendChild(createCheckerEl(c)));
  }
  const tr = root.querySelector('[data-drop-zone="tray-right"] .bg-zone-stack');
  if (tr) {
    tr.replaceChildren();
    state.trayRight.forEach((c) => tr.appendChild(createCheckerEl(c)));
  }

  const d = state.dice ?? { brown: /** @type {[number, number]} */ ([1, 1]), white: /** @type {[number, number]} */ ([1, 1]) };
  if (!isDiceSpinning(root, "brown")) renderDice(root, "brown", d.brown);
  if (!isDiceSpinning(root, "white")) renderDice(root, "white", d.white);

  wireCheckersDrag(root, ctx);
}

/**
 * @param {HTMLElement} root
 * @returns {GameState}
 */
export function readStateFromBoardDom(root) {
  /** @type {GameState} */
  const state = {
    stateVersion: 1,
    points: {},
    bar: [],
    trayLeft: [],
    trayRight: [],
    dice: getDiceValues(root),
  };
  for (let i = 1; i <= 24; i += 1) {
    const stack = root.querySelector(`[data-point="${i}"] .bg-stack`);
    if (!stack) {
      state.points[i] = [];
      continue;
    }
    state.points[i] = [...stack.querySelectorAll(CHECKER_SELECTOR)].map((el) =>
      el.classList.contains("light") ? "light" : "dark"
    );
  }
  const readZone = (sel) =>
    [...root.querySelectorAll(`${sel} ${CHECKER_SELECTOR}`)].map((el) =>
      el.classList.contains("light") ? "light" : "dark"
    );
  state.bar = readZone('[data-drop-zone="bar"]');
  state.trayLeft = readZone('[data-drop-zone="tray-left"]');
  state.trayRight = readZone('[data-drop-zone="tray-right"]');
  return state;
}

/** @type {HTMLElement | null} */
let draggedChecker = null;

/** @type {BoardWireContext | null} */
let activeDragCtx = null;

/** @type {string | null} */
let lastHoverZone = null;

/** @type {PiecePickInfo | null} */
let activePick = null;

/**
 * @param {HTMLElement} el
 */
function getDropZoneEl(el) {
  if (!el) return null;
  if (el.dataset.dropZone) return /** @type {HTMLElement} */ (el);
  return el.closest("[data-drop-zone]");
}

/**
 * @param {HTMLElement} checker
 * @returns {PiecePickInfo | null}
 */
function describeCheckerPick(checker) {
  const stack = checker.parentElement;
  const zone = getDropZoneEl(checker);
  if (!stack || !zone?.dataset.dropZone) return null;
  const stackIndex = [...stack.querySelectorAll(CHECKER_SELECTOR)].indexOf(checker);
  const color = checker.classList.contains("light") ? "light" : "dark";
  return {
    fromZone: zone.dataset.dropZone,
    stackIndex: stackIndex >= 0 ? stackIndex : 0,
    color: /** @type {CheckerColor} */ (color),
  };
}

/**
 * @param {HTMLElement} root
 */
export function clearRemotePresence(root) {
  root.querySelectorAll(".bg-drop-target-remote").forEach((z) => z.classList.remove("bg-drop-target-remote"));
  root.querySelectorAll(".bg-checker-source-remote").forEach((c) => c.classList.remove("bg-checker-source-remote"));
  root.querySelectorAll(".bg-presence-layer .bg-checker-ghost-opponent").forEach((g) => g.remove());
}

/**
 * @param {HTMLElement} root
 * @param {PiecePickInfo} pick
 * @returns {HTMLElement | null}
 */
function getCheckerRefForPick(root, pick) {
  const zone = root.querySelector(`[data-drop-zone="${pick.fromZone}"]`);
  if (!zone) return null;
  const stack = zone.querySelector(".bg-stack, .bg-zone-stack");
  if (!stack) return null;

  const checkers = [...stack.querySelectorAll(CHECKER_SELECTOR)];
  return checkers[pick.stackIndex] ?? checkers[checkers.length - 1] ?? null;
}

/**
 * @param {HTMLElement} root
 * @param {PiecePickInfo} pick
 */
function highlightRemoteSource(root, pick) {
  const ref = getCheckerRefForPick(root, pick);
  ref?.classList.add("bg-checker-source-remote");
}

/**
 * @param {HTMLElement} root
 * @param {PiecePickInfo} pick
 */
function placeRemoteDragGhost(root, pick) {
  const layer = root.querySelector(".bg-presence-layer");
  const ref = getCheckerRefForPick(root, pick);
  if (!layer || !ref) return;

  const ghost = document.createElement("div");
  ghost.className = `bg-checker bg-checker-ghost-opponent ${pick.color}`;
  ghost.setAttribute("aria-hidden", "true");

  const layerRect = layer.getBoundingClientRect();
  const refRect = ref.getBoundingClientRect();
  ghost.style.left = `${refRect.left - layerRect.left}px`;
  ghost.style.top = `${refRect.top - layerRect.top}px`;
  ghost.style.width = `${refRect.width}px`;
  ghost.style.height = `${refRect.height}px`;

  layer.appendChild(ghost);
}

/**
 * @param {HTMLElement} root
 * @param {PiecePickInfo} pick
 */
export function showRemotePiecePick(root, pick) {
  clearRemotePresence(root);
  highlightRemoteSource(root, pick);
  placeRemoteDragGhost(root, pick);
}

/**
 * @param {HTMLElement} root
 * @param {string} toZone
 */
export function showRemotePieceHover(root, toZone) {
  root.querySelectorAll(".bg-drop-target-remote").forEach((z) => z.classList.remove("bg-drop-target-remote"));
  root.querySelector(`[data-drop-zone="${toZone}"]`)?.classList.add("bg-drop-target-remote");
}

/**
 * @param {HTMLElement} root
 * @param {BoardWireContext} ctx
 */
function wireCheckersDrag(root, ctx) {
  activeDragCtx = ctx;
  lastHoverZone = null;

  root.querySelectorAll(CHECKER_SELECTOR).forEach((checker) => {
    checker.removeEventListener("dragstart", onDragStart);
    checker.removeEventListener("dragend", onDragEnd);
    checker.addEventListener("dragstart", onDragStart);
    checker.addEventListener("dragend", onDragEnd);
  });

  root.querySelectorAll("[data-drop-zone]").forEach((zone) => {
    zone.removeEventListener("dragover", onDragOver);
    zone.removeEventListener("dragleave", onDragLeave);
    zone.removeEventListener("drop", onDropHandler);
    zone.addEventListener("dragover", onDragOver);
    zone.addEventListener("dragleave", onDragLeave);
    zone.addEventListener("drop", onDropHandler);
  });
}

/** @param {DragEvent} event */
function onDragStart(event) {
  const t = /** @type {HTMLElement | null} */ (event.target);
  if (!t || !t.classList.contains("bg-checker")) return;
  draggedChecker = t;
  draggedChecker.classList.add("dragging");
  event.dataTransfer?.setData("text/plain", "checker");
  if (event.dataTransfer) event.dataTransfer.effectAllowed = "move";

  const pick = describeCheckerPick(t);
  if (pick) {
    activePick = pick;
    activeDragCtx?.onPiecePick?.(pick);
  }
}

/** @param {DragEvent} event */
function onDragEnd(event) {
  const t = /** @type {HTMLElement | null} */ (event.target);
  if (t?.classList.contains("bg-checker")) {
    t.classList.remove("dragging");
  }
  document.querySelectorAll(".bg-drop-target").forEach((z) => z.classList.remove("bg-drop-target"));
  if (activePick && draggedChecker) {
    activeDragCtx?.onPieceMove?.({
      pick: activePick,
      toZone: activePick.fromZone,
      reverted: true,
    });
  }
  draggedChecker = null;
  lastHoverZone = null;
  activePick = null;
}

/** @param {DragEvent} event */
function onDragOver(event) {
  if (!draggedChecker) return;
  event.preventDefault();
  if (event.dataTransfer) event.dataTransfer.dropEffect = "move";
  const el = /** @type {HTMLElement} */ (event.currentTarget);
  const zone = getDropZoneEl(el);
  zone?.classList.add("bg-drop-target");

  const toZone = zone?.dataset.dropZone;
  if (toZone && toZone !== lastHoverZone) {
    lastHoverZone = toZone;
    activeDragCtx?.onPieceHover?.(toZone);
  }
}

/** @param {DragEvent} event */
function onDragLeave(event) {
  const el = /** @type {HTMLElement} */ (event.currentTarget);
  const zone = getDropZoneEl(el);
  zone?.classList.remove("bg-drop-target");
}

/** @param {DragEvent} event */
function onDropHandler(event) {
  event.preventDefault();
  if (!draggedChecker) return;
  const el = /** @type {HTMLElement} */ (event.currentTarget);
  const zone = getDropZoneEl(el);
  if (!zone) return;
  const targetStack = zone.querySelector(".bg-stack, .bg-zone-stack");
  if (!targetStack) return;

  if (zone.classList.contains("bottom")) {
    targetStack.prepend(draggedChecker);
  } else {
    targetStack.appendChild(draggedChecker);
  }
  zone.classList.remove("bg-drop-target");

  const pick = activePick;
  const toZone = zone.dataset.dropZone ?? "";
  if (pick && toZone) {
    const reverted = toZone === pick.fromZone;
    activeDragCtx?.onPieceMove?.({ pick, toZone, reverted });
  }

  draggedChecker.classList.remove("dragging");
  draggedChecker = null;
  lastHoverZone = null;
  activePick = null;
  activeDragCtx?.afterDrop?.();
}

/**
 * @param {HTMLElement} container
 * @param {Partial<{
 *   initialState: GameState | object,
 *   onLocalChange: (s: GameState) => void,
 *   onPiecePick: (pick: PiecePickInfo) => void,
 *   onPieceHover: (toZone: string) => void,
 *   onPieceMove: (move: PieceMoveInfo) => void,
 *   onRollStart: (player: 'brown' | 'white', values: [number, number], rollId: string) => void,
 *   onRollComplete: (player: 'brown' | 'white', values: [number, number]) => void,
 * }>} options
 */
export function mountBackgammonBoard(container, options = {}) {
  const initial = options.initialState
    ? parseGameState(options.initialState)
    : createInitialGameState();

  const wrap = document.createElement("div");
  wrap.className = "bg-board-wrap";
  wrap.innerHTML = `
    <h2 class="sr-only">Backgammon Board</h2>
    <div class="bg-board-layout">
      <div class="bg-left-stack">
      <section class="bg-dice-panel" aria-label="Dice rollers">
        <div class="bg-player-dice" data-player="brown">
          <div class="bg-dice-header">
            <span class="sr-only">Brown Player</span>
            <button class="bg-roll-btn" type="button" data-roll-player="brown" aria-label="Roll brown player's dice">
              <span class="sr-only">Roll</span>
            </button>
          </div>
          <div class="bg-dice-row" aria-label="Brown dice">
            <div class="bg-die brown" data-die-player="brown" data-die-index="0"></div>
            <div class="bg-die brown" data-die-player="brown" data-die-index="1"></div>
          </div>
        </div>
        <div class="bg-player-dice" data-player="white">
          <div class="bg-dice-header">
            <span class="sr-only">White Player</span>
            <button class="bg-roll-btn" type="button" data-roll-player="white" aria-label="Roll white player's dice">
              <span class="sr-only">Roll</span>
            </button>
          </div>
          <div class="bg-dice-row" aria-label="White dice">
            <div class="bg-die white" data-die-player="white" data-die-index="0"></div>
            <div class="bg-die white" data-die-player="white" data-die-index="1"></div>
          </div>
        </div>
      </section>
      <div class="bg-chat-mount" aria-label="Chat mount"></div>
      </div>
      <div class="bg-board">
        <div class="bg-playfield">
          <aside class="bg-tray" data-drop-zone="tray-left" aria-label="Player one bear off">
            <span class="bg-zone-label sr-only">Bear Off</span>
            <div class="bg-zone-stack"></div>
          </aside>
          <section class="bg-quadrant" id="bg-left-quadrant" aria-label="Left points"></section>
          <section class="bg-bar" data-drop-zone="bar" aria-label="Bar">
            <span class="bg-zone-label sr-only">Bar</span>
            <div class="bg-zone-stack"></div>
          </section>
          <section class="bg-quadrant" id="bg-right-quadrant" aria-label="Right points"></section>
          <aside class="bg-tray" data-drop-zone="tray-right" aria-label="Player two bear off">
            <span class="bg-zone-label sr-only">Bear Off</span>
            <div class="bg-zone-stack"></div>
          </aside>
          <div class="bg-presence-layer" aria-hidden="true"></div>
        </div>
      </div>
    </div>
    <p class="sr-only">Drag and drop checkers between points.</p>
  `;

  const leftQ = wrap.querySelector("#bg-left-quadrant");
  const rightQ = wrap.querySelector("#bg-right-quadrant");
  if (leftQ && rightQ) {
    TOP_LEFT.forEach((n) => leftQ.appendChild(createPointEl(n, true, initial)));
    BOTTOM_LEFT.forEach((n) => leftQ.appendChild(createPointEl(n, false, initial)));
    TOP_RIGHT.forEach((n) => rightQ.appendChild(createPointEl(n, true, initial)));
    BOTTOM_RIGHT.forEach((n) => rightQ.appendChild(createPointEl(n, false, initial)));
  }

  const wireCtx = {
    afterDrop: () => {
      if (options.onLocalChange) {
        options.onLocalChange(readStateFromBoardDom(wrap));
      }
    },
    onPiecePick: options.onPiecePick,
    onPieceHover: options.onPieceHover,
    onPieceMove: options.onPieceMove,
  };

  renderStateIntoBoard(wrap, initial, wireCtx);
  bindDiceRollers(wrap, {
    onChange: wireCtx.afterDrop,
    onRollStart: options.onRollStart,
    onRollComplete: options.onRollComplete,
  });

  function syncCheckerSize() {
    const sample = wrap.querySelector(".bg-point .bg-stack");
    if (!sample) return;
    const size = Math.max(20, Math.floor(sample.clientWidth));
    document.documentElement.style.setProperty("--checker-size", `${size}px`);
  }
  syncCheckerSize();
  window.addEventListener("resize", syncCheckerSize);

  container.appendChild(wrap);

  const chatMount = wrap.querySelector(".bg-chat-mount");

  return {
    root: wrap,
    /** @type {HTMLElement | null} */
    chatMount,
    getState: () => readStateFromBoardDom(wrap),
    /** @param {GameState | object} s */
    setState(s) {
      renderStateIntoBoard(wrap, parseGameState(s), wireCtx);
      syncCheckerSize();
    },
    destroy() {
      window.removeEventListener("resize", syncCheckerSize);
      clearRemotePresence(wrap);
      wrap.remove();
    },
  };
}
